import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, ArrowRight, X, Check, Zap, Shield, Clock, Heart, Sparkles } from 'lucide-react';
import Footer from '@/components/shared/Footer';

const IMAGES = {
  hero: "https://images.pexels.com/photos/35290663/pexels-photo-35290663.png?auto=compress&cs=tinysrgb&w=800",
  bowl: "https://images.pexels.com/photos/29064083/pexels-photo-29064083.jpeg?auto=compress&cs=tinysrgb&w=800",
  lifestyle: "https://images.pexels.com/photos/8507670/pexels-photo-8507670.jpeg?auto=compress&cs=tinysrgb&w=800",
};

export default function MethodPage() {
  const { language } = useLanguage();
  const nav = useNavigate();
  const de = language === 'de';

  return (
    <div className="min-h-screen bg-bone" data-testid="method-page">
      {/* Back nav */}
      <div className="max-w-5xl mx-auto px-6 pt-8">
        <button onClick={() => nav('/')} className="flex items-center gap-2 text-foreground/60 hover:text-foreground font-body text-sm transition-colors" data-testid="method-back-btn">
          <ArrowLeft size={16} /> <span className="font-display text-xl font-semibold"><span className="text-terracotta">bel</span>osophie</span>
        </button>
      </div>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-12 pb-8 text-center">
        <p className="font-body text-sm uppercase tracking-[0.2em] text-terracotta font-semibold mb-3">
          {de ? 'DIE METHODE' : 'THE METHOD'}
        </p>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1]">
          {de ? 'Metabolische Ernährung' : 'Metabolic Nutrition'}
        </h1>
        <p className="font-body text-base md:text-lg text-foreground/50 mt-5 max-w-2xl mx-auto leading-relaxed">
          {de
            ? 'Kein Kalorienzählen. Kein Verzicht. Sondern verstehen, wie dein Stoffwechsel funktioniert – und ihn für dich arbeiten lassen.'
            : 'No calorie counting. No deprivation. Instead, understand how your metabolism works – and make it work for you.'}
        </p>
      </section>

      {/* Hero Image */}
      <section className="max-w-5xl mx-auto px-6 pb-12">
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img src={IMAGES.hero} alt={de ? 'Mediterrane Ernährung' : 'Mediterranean nutrition'} className="w-full h-[300px] md:h-[400px] object-cover" />
        </div>
      </section>

      {/* Was es NICHT ist */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl font-medium tracking-tight mb-6">
              {de ? 'Was es ' : 'What it '}<span className="text-red-400">{de ? 'nicht' : 'is not'}</span>{de ? ' ist' : ''}
            </h2>
            <div className="space-y-4">
              {(de ? [
                'Keine Diät mit Ablaufdatum',
                'Kein Kalorienzählen und Abwiegen',
                'Kein Verzicht auf Genuss',
                'Keine Verbote – kein schlechtes Gewissen',
                'Kein Programm von 25-jährigen Influencern',
              ] : [
                'Not a diet with an expiration date',
                'No calorie counting and weighing',
                'No giving up enjoyment',
                'No bans – no guilt',
                'Not a program from 25-year-old influencers',
              ]).map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-red-50/50 border border-red-200/30">
                  <X size={18} className="text-red-400 shrink-0" />
                  <p className="font-body text-sm text-foreground/70">{item}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Was es IST */}
          <div>
            <h2 className="font-display text-3xl font-medium tracking-tight mb-6">
              {de ? 'Was es ' : 'What it '}<span className="text-green-600">{de ? 'ist' : 'is'}</span>
            </h2>
            <div className="space-y-4">
              {(de ? [
                'Ein Verständnis für deinen Stoffwechsel',
                'Essen, das satt macht UND schlank hält',
                'Nachhaltige Gewohnheiten statt Jo-Jo-Effekt',
                'Freiheit: auch mal Pasta, Schokolade, Wein',
                'Entwickelt von einer Frau 55+, die es lebt',
              ] : [
                'Understanding your metabolism',
                'Food that satisfies AND keeps you slim',
                'Sustainable habits instead of yo-yo effect',
                'Freedom: also pasta, chocolate, wine sometimes',
                'Developed by a woman 55+ who lives it',
              ]).map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-green-50/50 border border-green-200/30">
                  <Check size={18} className="text-green-600 shrink-0" />
                  <p className="font-body text-sm text-foreground/70">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 5 Prinzipien */}
      <section className="bg-warmgray/50">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <p className="font-body text-sm uppercase tracking-widest text-sage font-semibold mb-3">
              {de ? 'BELINDAS PRINZIPIEN' : "BELINDA'S PRINCIPLES"}
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight">
              {de ? '5 Prinzipien, die funktionieren' : '5 principles that work'}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(de ? [
              { icon: Zap, num: '01', title: 'Protein-First', desc: 'Jede Mahlzeit beginnt mit Protein. Das sättigt, erhält deine Muskelmasse und kurbelt den Stoffwechsel an. Mindestens 100g Protein pro Tag – aus Fisch, Eiern, Hüttenkäse, Hülsenfrüchten.' },
              { icon: Shield, num: '02', title: 'Blutzucker stabil', desc: 'Stabile Blutzuckerwerte = kein Heisshunger. Wie? Kohlenhydrate immer mit Protein oder gesunden Fetten kombinieren. Zucker reduzieren, nicht eliminieren.' },
              { icon: Heart, num: '03', title: 'Entzündungshemmend', desc: 'Omega-3 (Lachs, Leinöl), Antioxidantien (Beeren, Gemüse), Kurkuma. Weniger Zucker, weniger Weissmehl. Besonders wichtig bei Hashimoto und in der Perimenopause.' },
              { icon: Clock, num: '04', title: 'Mahlzeiten-Timing', desc: 'Dein Stoffwechsel hat einen Rhythmus. Frühstück mit Protein, letzte Mahlzeit 3 Stunden vor dem Schlafen. Kein ständiges Snacking – lass deinem Körper Pausen.' },
              { icon: Sparkles, num: '05', title: '80/20 Genuss-Regel', desc: '80% nährstoffreich, 20% Genuss. Ein Glas Wein, ein Stück Schokolade, Pasta am Sonntag – ohne schlechtes Gewissen. Weil Nachhaltigkeit nur mit Freude funktioniert.' },
            ] : [
              { icon: Zap, num: '01', title: 'Protein-First', desc: 'Every meal starts with protein. It satisfies, preserves muscle mass and boosts metabolism. At least 100g protein per day – from fish, eggs, cottage cheese, legumes.' },
              { icon: Shield, num: '02', title: 'Stable blood sugar', desc: 'Stable blood sugar = no cravings. How? Always combine carbs with protein or healthy fats. Reduce sugar, don\'t eliminate it.' },
              { icon: Heart, num: '03', title: 'Anti-inflammatory', desc: 'Omega-3 (salmon, flaxseed oil), antioxidants (berries, vegetables), turmeric. Less sugar, less white flour. Especially important with Hashimoto and perimenopause.' },
              { icon: Clock, num: '04', title: 'Meal timing', desc: 'Your metabolism has a rhythm. Protein breakfast, last meal 3 hours before sleep. No constant snacking – give your body breaks.' },
              { icon: Sparkles, num: '05', title: '80/20 enjoyment rule', desc: '80% nutrient-rich, 20% enjoyment. A glass of wine, a piece of chocolate, pasta on Sunday – without guilt. Because sustainability only works with joy.' },
            ]).map((p, i) => (
              <Card key={i} className={`bg-white border border-border/60 rounded-2xl p-6 ${i === 4 ? 'lg:col-span-1 md:col-span-2 lg:col-span-1' : ''}`} data-testid={`principle-${i}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-terracotta/10 flex items-center justify-center shrink-0">
                    <p.icon size={20} className="text-terracotta" />
                  </div>
                  <span className="font-display text-3xl font-bold text-foreground/8">{p.num}</span>
                </div>
                <h3 className="font-display text-lg font-medium mb-2">{p.title}</h3>
                <p className="font-body text-sm text-foreground/60 leading-relaxed">{p.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Warum es funktioniert */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <img src={IMAGES.bowl} alt={de ? 'Metabolische Bowl' : 'Metabolic bowl'} className="w-full h-[350px] object-cover" />
          </div>
          <div className="space-y-5">
            <p className="font-body text-sm uppercase tracking-widest text-terracotta font-semibold">{de ? 'WISSENSCHAFTLICH FUNDIERT' : 'EVIDENCE-BASED'}</p>
            <h2 className="font-display text-3xl font-medium tracking-tight">
              {de ? 'Warum es funktioniert' : 'Why it works'}
            </h2>
            <p className="font-body text-foreground/60 leading-relaxed">
              {de
                ? 'Metabolische Ernährung basiert auf dem Prinzip, dass nicht die Kalorien allein entscheiden, sondern wie dein Körper sie verarbeitet. Protein erhöht die Thermogenese (du verbrennst mehr beim Verdauen), stabile Blutzuckerwerte verhindern Fetteinlagerung, und entzündungshemmende Lebensmittel unterstützen deinen Hormonhaushalt.'
                : 'Metabolic nutrition is based on the principle that it\'s not calories alone that matter, but how your body processes them. Protein increases thermogenesis (you burn more while digesting), stable blood sugar prevents fat storage, and anti-inflammatory foods support your hormonal balance.'}
            </p>
            <p className="font-body text-foreground/60 leading-relaxed">
              {de
                ? 'Gerade für Frauen 40+ mit verlangsamtem Stoffwechsel, Hashimoto oder in der Perimenopause ist dieser Ansatz besonders wirksam – weil er mit deinem Körper arbeitet, nicht gegen ihn.'
                : 'Especially for women 40+ with a slowed metabolism, Hashimoto or in perimenopause, this approach is particularly effective – because it works with your body, not against it.'}
            </p>
          </div>
        </div>
      </section>

      {/* Belindas Erfahrung */}
      <section className="bg-warmgray/50">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-5">
              <p className="font-body text-sm uppercase tracking-widest text-sage font-semibold">{de ? 'AUS EIGENER ERFAHRUNG' : 'FROM PERSONAL EXPERIENCE'}</p>
              <h2 className="font-display text-3xl font-medium tracking-tight">
                {de ? '„So halte ich seit Jahren mein Gewicht"' : '"This is how I\'ve maintained my weight for years"'}
              </h2>
              <p className="font-body text-foreground/60 leading-relaxed">
                {de
                  ? '„Ich habe alles durch: Low Carb, Intervallfasten, Kalorienzählen. Funktioniert hat auf Dauer nichts. Bis ich verstanden habe: Es geht nicht um weniger essen, sondern um das Richtige zur richtigen Zeit."'
                  : '"I\'ve tried everything: Low carb, intermittent fasting, calorie counting. Nothing worked long-term. Until I understood: It\'s not about eating less, but about the right thing at the right time."'}
              </p>
              <p className="font-body text-foreground/60 leading-relaxed">
                {de
                  ? '„Heute esse ich mehr als je zuvor – aber metabolisch optimiert. Protein zum Frühstück, Gemüse und gute Fette, abends leichter. Und ja, zwischendurch ein Glas Wein. Weil es kein Projekt ist, sondern mein Leben."'
                  : '"Today I eat more than ever – but metabolically optimized. Protein for breakfast, vegetables and healthy fats, lighter in the evening. And yes, a glass of wine in between. Because it\'s not a project, it\'s my life."'}
              </p>
              <p className="font-body text-foreground/80 font-medium italic">— Belinda</p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img src={IMAGES.lifestyle} alt={de ? 'Genuss & Lifestyle' : 'Enjoyment & lifestyle'} className="w-full h-[350px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 py-16 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-4">
          {de ? 'Bereit für deinen Stoffwechsel-Boost?' : 'Ready for your metabolism boost?'}
        </h2>
        <p className="font-body text-foreground/50 mb-8 max-w-xl mx-auto">
          {de ? 'Starte kostenlos und entdecke, wie metabolische Ernährung deinen Alltag verändert.' : 'Start free and discover how metabolic nutrition changes your daily life.'}
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Button onClick={() => nav('/auth?mode=register')} className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-8 py-6 text-lg font-medium shadow-lg shadow-terracotta/20 hover:scale-[1.02] transition-all" data-testid="method-cta">
            {de ? 'Kostenlos starten' : 'Start free'} <ArrowRight size={20} className="ml-2" />
          </Button>
          <Button onClick={() => nav('/about')} variant="outline" className="rounded-full border-terracotta/30 text-terracotta px-8 py-6 text-lg hover:bg-terracotta/5 transition-all">
            {de ? 'Über Belinda' : 'About Belinda'}
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
